import { create } from 'zustand';
import { ErrorStates } from '@src/utils/enums';
import { produce } from 'immer';
import _isEqual from 'lodash/isEqual';
import { MapGeometry, ProjectBounds } from '@api/types';

interface SectionState {
  id: number;
  dirty?: boolean;
  touched?: boolean;
  valid?: boolean;
  status?: string;
  label?: string;
  parentId?: number;
  isParentStep?: boolean;
  isSubStep?: boolean;
  hidden?: boolean;
}

type SectionsState = SectionState[];

interface RequestStore {
  addSections: (sections: SectionState[]) => void;
  arCsvData: string | null;
  getActiveSection: () => SectionState | undefined;
  getChildrenHiddenState: (parentId: number) => boolean;
  getSectionParentId: (id: number) => number | undefined;
  getSections: () => SectionState[];
  getSelectedRequestId: () => string;
  isAnySectionDirty: () => boolean;
  isFormReadOnly: boolean;
  isFormValid: () => boolean;
  isNewRequest: boolean;
  isSectionHidden: (id: number) => boolean;
  isSectionTouched: (id: number) => boolean;
  isSectionValid: (id: number) => boolean;
  projectBounds: ProjectBounds | null;
  projectGeometry: MapGeometry | null;
  resetActiveSection: () => void;
  resetDirtySections: () => void;
  resetRequest: () => void;
  sections: SectionsState;
  selectedRequestId: string;
  setActiveSection: (newActiveSection: SectionState) => void;
  setArCsvData: (csvData: string | null) => void;
  setIsFormReadOnly: (isReadOnly: boolean) => void;
  setIsNewRequest: (isNewRequest: boolean) => void;
  setProjectBounds: (bounds: ProjectBounds | null) => void;
  setProjectGeometry: (geometry: MapGeometry | null) => void;
  setSelectedRequestId: (formId: string) => void;
  updateSection: (id: number, updates: Partial<Omit<SectionState, 'id'>>) => void;
  updateSectionProps: (id: number, updates: Partial<Omit<SectionState, 'id'>>) => void;
}

const defaultValues = {
  isFormReadOnly: false,
  isNewRequest: false,
  projectBounds: null,
  projectGeometry: null,
  sections: [],
  selectedRequestId: '',
  arCsvData: null,
};

export const useRequest = create<RequestStore>((set, get) => ({
  ...defaultValues,

  addSections: (sections) =>
    set((state) => {
      // Keep only the sections from state that don't have matching ids in the new sections
      if (!sections.length) {
        return state;
      }
      return {
        sections: sections.map((newSection) => {
          const existingSection = state.sections.find((section) => section.id === newSection.id);

          if (!existingSection) {
            return newSection;
          }
          // Add all the new sections
          return {
            // Preserving existing section state here is intentional for browser back/forward navigation
            ...newSection,
            dirty: existingSection.dirty ?? newSection.dirty,
            touched: existingSection.touched ?? newSection.touched,
            valid: existingSection.valid ?? newSection.valid,
            status: existingSection.status ?? newSection.status,
            hidden: existingSection.hidden ?? newSection.hidden,
            label: newSection.label,
            parentId: newSection.parentId,
            isParentStep: newSection.isParentStep,
            isSubStep: newSection.isSubStep,
          };
        }),
      };
    }),

  getActiveSection: () =>
    get().sections.find(
      (section) =>
        section.status === ErrorStates.Active ||
        section.status === ErrorStates.ActiveComplete ||
        section.status === ErrorStates.ActiveError
    ),

  getChildrenHiddenState(parentId) {
    const subSteps = get().sections.filter((section) => section.parentId === parentId);
    return subSteps.every((subStep) => subStep.hidden === true);
  },

  getSections: () => get().sections,

  getSectionParentId: (id) => get().sections[id]?.parentId,

  getSelectedRequestId: () => get().selectedRequestId,

  isAnySectionDirty: () => get().sections?.some((section) => section?.dirty === true),
  isFormValid: () => get().sections?.every((section) => section?.valid === true),
  isSectionHidden: (id) => get().sections?.[id]?.hidden ?? false,
  isSectionTouched: (id) => get().sections?.[id]?.touched ?? false,
  isSectionValid: (id) => get().sections?.[id]?.valid ?? true,

  setActiveSection: (newActiveSection) =>
    set((state) => ({
      sections: state.sections.map((section) => {
        const status = section?.status ?? '';

        if (section.id === newActiveSection.id) {
          return {
            ...section,
            status: section.valid ? ErrorStates.ActiveComplete : ErrorStates.ActiveError,
          };
        }

        if (['complete', 'activecomplete'].includes(status)) {
          return { ...section, status: ErrorStates.Complete };
        }

        if (['error', 'activeerror'].includes(status)) {
          return { ...section, status: ErrorStates.Error };
        }

        if (section.id !== newActiveSection.id && ['active', ''].includes(status)) {
          return { ...section, status: '' };
        }

        return { ...section, status: section.status };
      }),
    })),

  setArCsvData: (csvData) =>
    set(() => ({
      arCsvData: csvData,
    })),

  setIsFormReadOnly: (isReadOnly) =>
    set(() => ({
      isFormReadOnly: isReadOnly,
    })),

  setIsNewRequest: (isNewRequest) =>
    set(() => ({
      isNewRequest,
    })),

  setProjectBounds: (bounds) =>
    set(() => ({
      projectBounds: bounds,
    })),

  setProjectGeometry: (geometry) =>
    set(() => ({
      projectGeometry: geometry,
    })),

  setSelectedRequestId: (formId) =>
    set(() => ({
      selectedRequestId: formId,
    })),

  resetActiveSection: () => {
    const sections = get().sections;
    sections.length && get().setActiveSection(sections[0]);
  },

  resetRequest: () => {
    const newSections = get().sections.length === 0 ? get().sections : [];

    return set(() => ({
      ...defaultValues,
      sections: newSections,
    }));
  },

  resetDirtySections: () =>
    set((state) => ({
      sections: state.sections.map((section) => ({
        ...section,
        dirty: false,
      })),
    })),

  updateSection: (id, updates) =>
    set((state) => {
      const sectionExists = state.sections.find((section) => section.id === id);

      if (sectionExists) {
        const updated = { ...sectionExists, ...updates };
        // Only update if something actually changed to prevent unnecessary re-renders
        if (_isEqual(updated, sectionExists)) {
          return state;
        }
        return {
          sections: state.sections.map((section) => (section.id === id ? updated : section)),
        };
      }
      return {
        sections: [...state.sections, { id, ...updates }],
      };
    }),

  updateSectionProps: (id, updates) =>
    set(
      produce((draft) => {
        const sectionIndex = draft.sections.findIndex((section: SectionState) => section.id === id);
        if (sectionIndex === -1) return;

        for (const key in updates) {
          draft.sections[sectionIndex][key] = updates[key as keyof typeof updates];
        }

        return draft;
      })
    ),
}));

export default useRequest;
